import React, { useState, useEffect } from 'react';

import {
  Button,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  CircularProgress,
} from '@mui/material';
import { makeStyles } from 'tss-react/mui';

import { FormNumberFormatField, FormTextField } from 'components/forms/FormikFields';
import { Formik, Form } from 'formik';

import yup from 'commons/validations';
import showNotification from 'commons/helpers/showNotification';
import CustomerCreditService from 'services/CustomerCreditService';
import { CREDITS_MOVEMENT_TYPE_ADD } from './customerCreditsHelpers';
import NumberUtils from 'commons/utils/numberUtils';
import LayoutStatus from 'components/containers/LayoutStatus';

const useStyles = makeStyles()((theme) => ({
  root: {
    padding: theme.spacing(3),
  },
  content: {
    marginTop: theme.spacing(2),
  },
  actionsRoot: {
    justifyContent: 'space-between',
  },

  headerTitle: {
    textAlign: 'center',
    color: 'white',
  },
  headerTitleContainer: {
    padding: 6,
    borderRadius: 5,
  },
}));

const schema = yup.object().shape({
  value: yup.number().nullable().required(),
  description: yup.string().max(255),
});
const DEFAULT_INIT_VALUES = { value: '', description: '', movementType: CREDITS_MOVEMENT_TYPE_ADD };

// COMPONENT
function CustomerCommentsFormModal({ open, onClose, object, onSave }) {
  const { classes } = useStyles();
  const [initialValues, setInitialValues] = useState(DEFAULT_INIT_VALUES);
  const [isWorking, setIsWorking] = useState(false);

  useEffect(() => {
    if (open) {
      setInitialValues({
        ...DEFAULT_INIT_VALUES,
        id: object.id,
        value: object.value ?? '',
        description: object.description ?? '',
        customerId: object.customerId,
        movementType: object.movementType,
        timestamp: new Date().getTime(),
      });
    }
  }, [open, object]);

  const myHandleSubmit = (values, actions) => {
    const isUpdating = object.id != null;
    setIsWorking(true);
    CustomerCreditService.save(values)
      .then((savedObj) => {
        showNotification('success', isUpdating ? 'Atualizado com sucesso.' : 'Criado com sucesso.');
        onSave(savedObj, isUpdating);
        onClose();
      })
      .finally(() => {
        actions.setSubmitting(false);
        setIsWorking(false);
      });
  };

  const addingCredits = object?.movementType === CREDITS_MOVEMENT_TYPE_ADD;
  const isEditing = object?.id != null;

  return (
    <Dialog onClose={onClose} aria-labelledby="customized-dialog-title" open={open} fullWidth maxWidth="xs">
      <Formik
        enableReinitialize
        initialValues={initialValues}
        validationSchema={schema}
        onSubmit={myHandleSubmit}
      >
        {({ dirty, isSubmitting, values }) => {
          return (
            <Form>
              <DialogTitle>
                <LayoutStatus
                  status={addingCredits ? 'success' : 'error'}
                  className={classes.headerTitleContainer}
                >
                  {isEditing ? (
                    <Typography variant="subtitle2" className={classes.headerTitle}>
                      {addingCredits
                        ? `Você está editando a descrição\ndo crédito adicionado de ${NumberUtils.toCurrency(
                            object?.value,
                          )}`
                        : `Você está editando a descrição\ndo crédito removido de ${NumberUtils.toCurrency(
                            object?.value,
                          )}`}
                    </Typography>
                  ) : (
                    <Typography variant="subtitle2" className={classes.headerTitle}>
                      {addingCredits ? 'Você está adicionando crédito' : 'Você está removendo crédito'}
                    </Typography>
                  )}
                </LayoutStatus>
              </DialogTitle>
              <DialogContent dividers>
                {isWorking && <CircularProgress size={20} thickness={5} />}

                {isEditing ? (
                  <Box display="flex" flexDirection="row" alignItems="center">
                    <FormTextField fullWidth label="Descrição" name="description" />
                  </Box>
                ) : (
                  <>
                    <Box display="flex" flexDirection="row" alignItems="center">
                      <FormNumberFormatField fullWidth label="Valor / Crédito" name="value" />
                    </Box>
                    <Box display="flex" flexDirection="row" alignItems="center">
                      <FormTextField fullWidth label="Descrição" name="description" />
                    </Box>
                  </>
                )}
              </DialogContent>

              <DialogActions
                classes={{
                  root: classes.actionsRoot,
                }}
              >
                <Button variant="outlined" onClick={() => onClose()}>
                  Fechar
                </Button>
                <Button variant="contained" type="submit" color="primary" disabled={!dirty || isSubmitting}>
                  Salvar
                </Button>
              </DialogActions>
            </Form>
          );
        }}
      </Formik>
    </Dialog>
  );
}

export default CustomerCommentsFormModal;
